// Gọi API bảng tin nội bộ (khothi_api_social). Dùng chung token đăng nhập CMS.
// Tài liệu: https://test.buso.asia/social/document/tai_lieu_api_social.html
// Dữ liệu API được đổi về kiểu Post trong socialStore để các component hiển thị giữ nguyên.
import { message } from 'antd';
import { SOCIAL_API_URL } from '../../env';
import { mixLogOut, mixToken } from 'services/mixin';
import type { Audience, FeedFilter, Me, Post, PostImage } from './socialStore';

const BASE = `${SOCIAL_API_URL}/api/v1`;

export class SocialApiError extends Error {
  constructor(msg: string, public status: number, public errors?: string[]) { super(msg); }
}

interface Envelope<T> { success: boolean; statusCode: number; message: string; data: T; meta?: PageMeta; errors?: string[] }
interface PageMeta { limit: number; hasMore: boolean; nextCursor: string | null }

export async function socialFetch<T = any>(path: string, opt: { method?: string; body?: unknown } = {}): Promise<Envelope<T>> {
  const isForm = opt.body instanceof FormData;
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method: opt.method ?? 'GET',
      headers: { Authorization: `Bearer ${mixToken() || ''}`, ...(opt.body && !isForm ? { 'Content-Type': 'application/json' } : {}) },
      // FormData: để trình duyệt tự thêm Content-Type kèm boundary
      body: isForm ? (opt.body as FormData) : opt.body ? JSON.stringify(opt.body) : undefined,
    });
  } catch {
    throw new SocialApiError('Không kết nối được máy chủ bảng tin, hãy thử lại', 0);
  }
  const json: Envelope<T> | null = await res.json().catch(() => null);
  if (!json?.success) {
    const status = json?.statusCode ?? res.status;
    if (status === 401) {
      // Chưa đăng nhập CMS thì về trang đăng nhập. Còn token CMS mà bảng tin (dịch vụ riêng) từ chối thì chỉ báo lỗi:
      // đăng xuất ở đây sẽ đá người dùng khỏi cả CMS chỉ vì bảng tin cấu hình sai / đang lỗi
      if (!mixToken()) mixLogOut();
      throw new SocialApiError('Bảng tin chưa nhận phiên đăng nhập của bạn, hãy tải lại trang hoặc đăng nhập lại', 401, json?.errors);
    }
    if (status === 413) throw new SocialApiError('Ảnh quá lớn (tối đa 10MB)', 413);
    // lỗi validate: message = "Bad Request", câu cụ thể nằm ở errors[0]
    const msg = json?.message === 'Bad Request' && json.errors?.length ? json.errors[0] : json?.message;
    throw new SocialApiError(status >= 500 || !msg ? 'Hệ thống bảng tin đang lỗi, hãy thử lại sau' : msg, status, json?.errors);
  }
  return json;
}

/** Nút đã có giao diện nhưng backend chưa có API (cảm xúc, bình luận, chia sẻ...) */
export const comingSoon = (what = 'Tính năng này') => message.info(`${what} đang được hoàn thiện, sẽ sớm có.`);

// ---------------------------------------------------------------------------
// Kiểu dữ liệu API
// ---------------------------------------------------------------------------
export interface ApiMe {
  userId: string; staffId: string | null; name: string; avatar: string | null; email: string | null;
  position: string | null; departmentName: string | null; branchName: string | null; type: number; isAdmin: boolean;
}

export interface ApiPost {
  id: string;
  kind: 'post' | 'recognition';
  author: { userId: string; staffId: string | null; name: string; avatar: string | null; organization: string | null };
  audiences: Audience[];
  title: string | null;
  html: string;
  images: PostImage[];
  recognition: { badge: string; people: { staffId: string; userId: string | null; name: string; avatar: string | null }[] } | null;
  mentions: { staffId: string; userId: string | null; name: string }[];
  sharedFrom: string | null;
  publishAt: string;
  createdAt: string;
  editedAt: string | null;
  scheduled: boolean;
  commentsLocked: boolean;
  views: number;
  reactionsCount: number;
  reactionTop: { emoji: string; count: number }[];
  commentsCount: number;
  me: { reaction: string | null; saved: boolean; muted: boolean; canEdit: boolean; canDelete: boolean };
}

export const toMe = (m: ApiMe): Me => ({
  id: m.userId, name: m.name, avatar: m.avatar, isAdmin: m.isAdmin,
  subtitle: m.position || m.departmentName || m.email || '',
});

export const toPost = (a: ApiPost): Post => ({
  id: a.id,
  authorId: a.author.userId,
  authorName: a.author.name,
  authorAvatar: a.author.avatar,
  organization: a.author.organization ?? undefined,
  audiences: a.audiences?.length ? a.audiences : ['organization'],
  kind: a.kind,
  title: a.title ?? undefined,
  html: a.html,
  images: (a.images || []).map(i => i.url),
  imageMeta: a.images || [],
  recognition: a.recognition
    ? { badge: a.recognition.badge, people: a.recognition.people.map(p => ({ id: p.staffId, userId: p.userId, name: p.name, avatar: p.avatar })) }
    : undefined,
  mentions: (a.mentions || []).map(m => ({ id: m.staffId, userId: m.userId, name: m.name })),
  sharedFrom: a.sharedFrom ?? undefined,
  createdAt: a.createdAt,
  publishAt: a.publishAt,
  editedAt: a.editedAt ?? undefined,
  commentsLocked: a.commentsLocked,
  views: a.views,
  // danh sách cảm xúc / người xem / bình luận chưa có API (Đợt 5) — chỉ có số liệu tổng ở `stats`
  viewedBy: [],
  reactions: {},
  comments: [],
  scheduled: a.scheduled,
  stats: { reactions: a.reactionsCount, reactionTop: a.reactionTop || [], comments: a.commentsCount },
  viewer: a.me,
});

// ---------------------------------------------------------------------------
// Các API đã có (Đợt 0 → Đợt 4)
// ---------------------------------------------------------------------------
export interface FeedPage { posts: Post[]; nextCursor: string | null; hasMore: boolean }

/** Sửa bài: gửi trường nào sửa trường đó. publishAt chỉ với bài còn hẹn giờ, null = đăng ngay. staffIds, badge chỉ với bài ghi nhận */
export interface PostPatch { title?: string; html?: string; images?: PostImage[]; commentsLocked?: boolean; publishAt?: string | null; staffIds?: number[]; badge?: string }

/** Một dòng bảng xếp hạng; userId = null với nhân sự chưa có tài khoản */
export interface LeaderRow { rank: number; staffId: string; userId: string | null; name: string; avatar: string | null; count: number }

const postPath = (id: string) => `/social/posts/${encodeURIComponent(id)}`;

export const feedApi = {
  me: () => socialFetch<ApiMe>('/me').then(r => toMe(r.data)),

  list: (p: { filter: FeedFilter; q?: string; cursor?: string | null; limit?: number }): Promise<FeedPage> => {
    const qs = new URLSearchParams({ limit: String(p.limit ?? 10), filter: p.filter });
    if (p.q?.trim()) qs.set('q', p.q.trim());
    if (p.cursor) qs.set('cursor', p.cursor);
    return socialFetch<ApiPost[]>(`/social/posts?${qs}`).then(r => ({
      posts: r.data.map(toPost), nextCursor: r.meta?.nextCursor ?? null, hasMore: !!r.meta?.hasMore,
    }));
  },

  get: (id: string) => socialFetch<ApiPost>(postPath(id)).then(r => toPost(r.data)),

  counters: () => socialFetch<{ mentions: number; scheduled: number }>('/social/posts/counters').then(r => r.data),

  /** @nhắc tên nằm trong html. Bài ghi nhận dùng createRecognition */
  create: (input: { title?: string; html: string; images: PostImage[]; commentsLocked: boolean; publishAt?: string }) =>
    socialFetch<ApiPost>('/social/posts', {
      method: 'POST',
      body: {
        title: input.title?.trim() || undefined, html: input.html, images: input.images.length ? input.images : undefined,
        audiences: ['organization'], commentsLocked: input.commentsLocked, publishAt: input.publishAt,
      },
    }).then(r => ({ post: toPost(r.data), message: r.message })),

  createRecognition: (input: { staffIds: number[]; badge: string; html: string; images: PostImage[]; commentsLocked: boolean; publishAt?: string }) =>
    socialFetch<ApiPost>('/social/recognitions', {
      method: 'POST',
      body: {
        staffIds: input.staffIds, badge: input.badge, html: input.html, images: input.images.length ? input.images : undefined,
        audiences: ['organization'], commentsLocked: input.commentsLocked, publishAt: input.publishAt,
      },
    }).then(r => ({ post: toPost(r.data), message: r.message })),

  /** null = chưa có ghi nhận nào */
  latestRecognition: () => socialFetch<ApiPost | null>('/social/recognitions/latest').then(r => (r.data ? toPost(r.data) : null)),

  leaderboard: (limit = 5) => socialFetch<LeaderRow[]>(`/social/recognitions/leaderboard?limit=${limit}`).then(r => r.data),

  /** 1–6 ảnh, mỗi ảnh ≤ 10MB; một tệp sai thì cả lượt không được lưu */
  uploadImages: (files: File[]) => {
    const form = new FormData();
    files.forEach(f => form.append('files', f));
    return socialFetch<PostImage[]>('/social/uploads/images', { method: 'POST', body: form }).then(r => r.data);
  },

  update: (id: string, body: PostPatch) =>
    socialFetch<ApiPost>(postPath(id), { method: 'PATCH', body }).then(r => ({ post: toPost(r.data), message: r.message })),

  remove: (id: string) => socialFetch<null>(postPath(id), { method: 'DELETE' }).then(r => r.message),

  scheduled: () => socialFetch<ApiPost[]>('/social/posts/scheduled').then(r => r.data.map(toPost)),

  publish: (id: string) =>
    socialFetch<ApiPost>(`${postPath(id)}/publish`, { method: 'POST' }).then(r => ({ post: toPost(r.data), message: r.message })),

  setCommentsLocked: (id: string, locked: boolean) =>
    socialFetch<ApiPost>(`${postPath(id)}/comments-lock`, { method: 'PATCH', body: { locked } }).then(r => toPost(r.data)),

  setSaved: (id: string, saved: boolean) =>
    socialFetch<{ saved: boolean }>(`${postPath(id)}/save`, { method: saved ? 'PUT' : 'DELETE' }).then(r => ({ saved: r.data.saved, message: r.message })),

  setHidden: (id: string, hidden: boolean) =>
    socialFetch<{ hidden: boolean }>(`${postPath(id)}/hide`, { method: hidden ? 'PUT' : 'DELETE' }).then(r => ({ hidden: r.data.hidden, message: r.message })),

  view: (id: string) => socialFetch<{ views: number; counted: boolean }>(`${postPath(id)}/views`, { method: 'POST' }).then(r => r.data),
};
